
/**
 * Utility for handling Google Maps API key access
 */

// Default API key as a fallback
const DEFAULT_API_KEY = 'AIzaSyARnqYqNIfpfKnLsUlyg9sbutAyCpJPjg0';

// Current environment domain
export const getCurrentDomain = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return '';
};

// Attempt to get the Google Maps API key from environment
export const getGoogleMapsApiKey = (): string => {
  // Safely access the environment variable, with fallback
  const apiKey = import.meta?.env?.VITE_GOOGLE_MAPS_API_KEY || DEFAULT_API_KEY;
  
  // Validate and clean the API key
  if (typeof apiKey === 'string' && apiKey) {
    // Direct string format - check if it's already valid
    if (apiKey.startsWith('AIza')) {
      return apiKey;
    }
    
    // Try to extract the key using regex if it contains the API key pattern
    if (apiKey.includes('AIza')) {
      try {
        const matches = apiKey.match(/AIza[a-zA-Z0-9_\-]+/);
        if (matches && matches[0]) {
          return matches[0];
        }
      } catch (e) {
        console.error("Error extracting Google Maps API key from encoded format:", e);
      }
    }
  }
  
  // If we have a problem, log detailed info to help debug
  console.group("🔑 API Key Debug Info");
  console.log("API Key present:", !!apiKey);
  console.log("API Key type:", typeof apiKey);
  if (apiKey && typeof apiKey === 'string') {
    console.log("API Key length:", apiKey.length);
    console.log("API Key first few chars:", apiKey.substring(0, Math.min(10, apiKey.length)));
    console.log("API Key includes 'AIza':", apiKey.includes('AIza'));
  }
  console.log("Environment:", import.meta?.env?.MODE || 'unknown');
  console.log("Base URL:", import.meta?.env?.BASE_URL || 'unknown');
  console.groupEnd();
  
  // Fallback to default key
  return DEFAULT_API_KEY;
};

// Export additional diagnostic function for debugging
export const diagnoseGoogleMapsApiKey = (): void => {
  const rawKey = import.meta?.env?.VITE_GOOGLE_MAPS_API_KEY;
  const processedKey = getGoogleMapsApiKey();
  const currentDomain = getCurrentDomain();
  
  console.group("🔍 Google Maps API Key Diagnostics");
  console.log("Raw key available:", !!rawKey);
  console.log("Raw key type:", typeof rawKey);
  if (rawKey && typeof rawKey === 'string') {
    console.log("Raw key length:", rawKey.length);
    console.log("Raw key preview:", rawKey.substring(0, Math.min(10, rawKey.length)) + "...");
    console.log("Raw key includes 'AIza':", rawKey.includes('AIza'));
  }
  
  console.log("Processed key available:", !!processedKey);
  console.log("Processed key length:", processedKey.length);
  if (processedKey) {
    console.log("Processed key starts with 'AIza':", processedKey.startsWith('AIza'));
    console.log("Processed key preview:", processedKey.substring(0, Math.min(10, processedKey.length)) + "...");
  }
  
  console.log("Current domain:", currentDomain);
  console.groupEnd();
  
  // Check if the key appears to be valid
  if (!processedKey) {
    console.error("⚠️ Google Maps API key is missing. Using default key.");
  } else if (!processedKey.startsWith('AIza')) {
    console.error("⚠️ Google Maps API key appears to be invalid. Keys should start with 'AIza'.");
  } else if (processedKey.length < 30) {
    console.error("⚠️ Google Maps API key appears to be too short. Google Maps API keys are typically longer.");
  } else {
    console.log("✅ Google Maps API key format appears valid.");
  }
};
